// MFA Verification Page

import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuthStore } from '../../stores/auth-store';
import { trpc } from '../../lib/trpc';

interface MfaFormData {
  code: string;
}

export function VerifyMfaPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuthStore();
  const email = (location.state as { email?: string })?.email;

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<MfaFormData>();

  const verifyMutation = trpc.auth.verifyMfa.useMutation({
    onSuccess: (data) => {
      login(data.accessToken, data.refreshToken, data.user, data.expiresAt);
      navigate('/dashboard');
    },
    onError: (error) => {
      setError('root', { message: error.message });
    },
  });

  const resendMutation = trpc.auth.sendMfaCode.useMutation({
    onSuccess: () => {
      alert('A new code has been sent to your email.');
    },
    onError: (error) => {
      setError('root', { message: error.message });
    },
  });

  const onSubmit = (data: MfaFormData) => {
    if (!email) {
      setError('root', { message: 'Session expired. Please log in again.' });
      return;
    }
    verifyMutation.mutate({
      email,
      code: data.code,
    });
  };

  const handleResend = () => {
    if (!email) {
      setError('root', { message: 'Session expired. Please log in again.' });
      return;
    }
    resendMutation.mutate({ email });
  };

  if (!email) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-12">
        <div className="card text-center">
          <h2 className="text-lg font-medium text-gray-900 mb-2">Session Expired</h2>
          <p className="text-sm text-gray-600 mb-4">Please log in again.</p>
          <Link to="/login" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
            Back to login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-12">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <img src="/calculom.png" alt="Calculom" className="w-[110px] h-auto mx-auto mb-4" />
          <p className="text-gray-600">Verify your identity</p>
        </div>

        <div className="card">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {errors.root && (
              <div className="rounded-lg bg-red-50 p-3 text-sm text-red-600">
                {errors.root.message}
              </div>
            )}

            <p className="text-sm text-gray-600">
              We've sent a 6-digit verification code to <strong>{email}</strong>
            </p>

            <div>
              <label htmlFor="code" className="label">
                Verification code
              </label>
              <input
                id="code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                className="input text-center text-2xl tracking-widest"
                placeholder="000000"
                {...register('code', {
                  required: 'Code is required',
                  pattern: { value: /^\d{6}$/, message: 'Code must be 6 digits' },
                })}
              />
              {errors.code && (
                <p className="mt-1 text-sm text-red-600">{errors.code.message}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={verifyMutation.isPending}
              className="btn-primary w-full"
            >
              {verifyMutation.isPending ? 'Verifying...' : 'Verify'}
            </button>

            <div className="text-center">
              <button
                type="button"
                onClick={handleResend}
                disabled={resendMutation.isPending}
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                {resendMutation.isPending ? 'Sending...' : "Didn't receive a code? Resend"}
              </button>
            </div>
          </form>

          <div className="mt-6 text-center text-sm text-gray-600">
            <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium">
              Back to login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
